/**
 * B11 — PrintPreviewDialog (PRINT / PLOT command).
 *
 * AutoCAD parity: PLOT command opens a "Plot — Page Setup" dialog that lets
 * the user pick:
 *   - Printer / plotter (we expose a simulated list — selection is purely
 *     informational since we don't ship a print driver)
 *   - Paper size (A4 / A3 / A2 / A1 / A0 + Letter / Tabloid)
 *   - Orientation (Portrait / Landscape)
 *   - Margins (left / right / top / bottom in millimetres)
 *   - Plot area (Extents / Layout / Window)
 *
 * While the dialog is open the host (App.tsx) keeps `printPreviewBounds`
 * populated on `Scene3DStage`, which surfaces a translucent rectangle on
 * the canvas (the existing `<PrintPreviewBounds>` overlay). Each control
 * change reflows the rectangle live so the user can see exactly what
 * portion of the model space falls inside the printable area before
 * committing.
 *
 * Fully presentational — the host owns dialog visibility, paper-size /
 * orientation / margin state, and the world→screen rectangle projection.
 */

import { useT } from "../i18n";

/**
 * Paper size identifier. Each size has a known mm width × height in
 * portrait orientation; landscape swaps width/height.
 */
export type PaperSizeId =
  | "A0"
  | "A1"
  | "A2"
  | "A3"
  | "A4"
  | "Letter"
  | "Tabloid";

export type PrintOrientation = "portrait" | "landscape";

export type PrintArea = "extents" | "layout" | "window";

export interface PrintMargins {
  readonly left: number;
  readonly right: number;
  readonly top: number;
  readonly bottom: number;
}

/**
 * Static catalogue of paper sizes (mm). Portrait dimensions; the dialog
 * computes the printable rectangle from these values + margins +
 * orientation choice.
 */
export const PAPER_SIZES_MM: Record<
  PaperSizeId,
  { readonly width: number; readonly height: number }
> = {
  A0: { width: 841, height: 1189 },
  A1: { width: 594, height: 841 },
  A2: { width: 420, height: 594 },
  A3: { width: 297, height: 420 },
  A4: { width: 210, height: 297 },
  Letter: { width: 215.9, height: 279.4 },
  Tabloid: { width: 279.4, height: 431.8 },
};

/**
 * Compute paper width × height in mm for the given orientation.
 * Portrait keeps the catalogue dimensions; landscape swaps them.
 */
export function resolvePaperDimensions(
  size: PaperSizeId,
  orientation: PrintOrientation,
): { width: number; height: number } {
  const base = PAPER_SIZES_MM[size];
  return orientation === "portrait"
    ? { width: base.width, height: base.height }
    : { width: base.height, height: base.width };
}

/**
 * Compute the printable area in mm given paper + margins. The result is
 * always non-negative (oversized margins clamp to zero).
 */
export function resolvePrintableMm(
  size: PaperSizeId,
  orientation: PrintOrientation,
  margins: PrintMargins,
): { width: number; height: number } {
  const { width, height } = resolvePaperDimensions(size, orientation);
  return {
    width: Math.max(width - margins.left - margins.right, 0),
    height: Math.max(height - margins.top - margins.bottom, 0),
  };
}

export interface PrintDialogSettings {
  readonly paperSize: PaperSizeId;
  readonly orientation: PrintOrientation;
  readonly margins: PrintMargins;
  readonly area: PrintArea;
}

export const DEFAULT_PRINT_SETTINGS: PrintDialogSettings = {
  paperSize: "A4",
  orientation: "portrait",
  margins: { left: 10, right: 10, top: 10, bottom: 10 },
  area: "extents",
};

export interface PrintPreviewDialogProps {
  readonly settings: PrintDialogSettings;
  readonly onChange: (next: PrintDialogSettings) => void;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
  /**
   * PDF-6.7 — invoked when the user clicks the "Указать рамку" / "Pick
   * window" button (shown only when plot-area = Window). AutoCAD parity:
   * the host hides this dialog and lets the user rubber-band the plot
   * window on the canvas, then re-shows the dialog with the window set.
   * Optional — when absent the button is not rendered.
   */
  readonly onPickWindow?: () => void;
}

const PAPER_OPTIONS: PaperSizeId[] = [
  "A0",
  "A1",
  "A2",
  "A3",
  "A4",
  "Letter",
  "Tabloid",
];

export function PrintPreviewDialog(
  props: PrintPreviewDialogProps,
): React.ReactElement {
  const t = useT();
  const { settings, onChange, onConfirm, onCancel, onPickWindow } = props;

  const updateMargin = (key: keyof PrintMargins, raw: string) => {
    const parsed = Number(raw);
    const value = Number.isFinite(parsed) ? Math.max(parsed, 0) : 0;
    onChange({
      ...settings,
      margins: { ...settings.margins, [key]: value },
    });
  };

  return (
    <div
      className="dimstyle-editor-overlay"
      data-testid="print-preview-dialog-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        className="dimstyle-editor"
        data-testid="print-preview-dialog"
        style={{ width: 520, maxHeight: "90vh", overflowY: "auto" }}
      >
        <div className="dimstyle-editor-title">{t("print.dialog.title")}</div>
        <div className="dimstyle-editor-row">
          <label
            htmlFor="print-printer"
            className="dimstyle-editor-label"
            style={{ width: 160 }}
          >
            {t("print.dialog.printer_label")}
          </label>
          <select
            id="print-printer"
            data-testid="print-printer-select"
            disabled
            value="virtual"
            onChange={() => {
              /* simulated single-choice */
            }}
          >
            <option value="virtual">DXF Editor (Virtual)</option>
          </select>
        </div>
        <div className="dimstyle-editor-row">
          <label
            htmlFor="print-paper-size"
            className="dimstyle-editor-label"
            style={{ width: 160 }}
          >
            {t("print.dialog.paper_size_label")}
          </label>
          <select
            id="print-paper-size"
            data-testid="print-paper-size-select"
            value={settings.paperSize}
            onChange={(e) =>
              onChange({
                ...settings,
                paperSize: e.target.value as PaperSizeId,
              })
            }
          >
            {PAPER_OPTIONS.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>
        <div className="dimstyle-editor-row">
          <label
            htmlFor="print-orientation"
            className="dimstyle-editor-label"
            style={{ width: 160 }}
          >
            {t("print.dialog.orientation_label")}
          </label>
          <select
            id="print-orientation"
            data-testid="print-orientation-select"
            value={settings.orientation}
            onChange={(e) =>
              onChange({
                ...settings,
                orientation: e.target.value as PrintOrientation,
              })
            }
          >
            <option value="portrait">
              {t("print.dialog.orientation.portrait")}
            </option>
            <option value="landscape">
              {t("print.dialog.orientation.landscape")}
            </option>
          </select>
        </div>
        <div className="dimstyle-editor-row">
          <label
            htmlFor="print-area"
            className="dimstyle-editor-label"
            style={{ width: 160 }}
          >
            {t("print.dialog.area_label")}
          </label>
          <select
            id="print-area"
            data-testid="print-area-select"
            value={settings.area}
            onChange={(e) =>
              onChange({
                ...settings,
                area: e.target.value as PrintArea,
              })
            }
          >
            <option value="extents">{t("print.dialog.area.extents")}</option>
            <option value="layout">{t("print.dialog.area.layout")}</option>
            <option value="window">{t("print.dialog.area.window")}</option>
          </select>
          {/* PDF-6.7 — Window-pick button (AutoCAD "Window<"). Shown only
              for plot-area = Window; clicking hides the dialog and starts a
              2-corner canvas pick. */}
          {settings.area === "window" && onPickWindow ? (
            <button
              type="button"
              data-testid="print-window-pick"
              onClick={onPickWindow}
              style={{ marginLeft: 8 }}
            >
              {t("print.dialog.window_pick")}
            </button>
          ) : null}
        </div>
        <div className="dimstyle-editor-row">
          <span className="dimstyle-editor-label" style={{ width: 160 }}>
            {t("print.dialog.margin_label")}
          </span>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "auto 80px auto 80px",
              gap: 8,
              alignItems: "center",
            }}
          >
            <label htmlFor="print-margin-left">
              {t("print.dialog.margin_left")}
            </label>
            <input
              id="print-margin-left"
              data-testid="print-margin-left"
              type="number"
              min={0}
              step={1}
              value={settings.margins.left}
              onChange={(e) => updateMargin("left", e.target.value)}
            />
            <label htmlFor="print-margin-right">
              {t("print.dialog.margin_right")}
            </label>
            <input
              id="print-margin-right"
              data-testid="print-margin-right"
              type="number"
              min={0}
              step={1}
              value={settings.margins.right}
              onChange={(e) => updateMargin("right", e.target.value)}
            />
            <label htmlFor="print-margin-top">
              {t("print.dialog.margin_top")}
            </label>
            <input
              id="print-margin-top"
              data-testid="print-margin-top"
              type="number"
              min={0}
              step={1}
              value={settings.margins.top}
              onChange={(e) => updateMargin("top", e.target.value)}
            />
            <label htmlFor="print-margin-bottom">
              {t("print.dialog.margin_bottom")}
            </label>
            <input
              id="print-margin-bottom"
              data-testid="print-margin-bottom"
              type="number"
              min={0}
              step={1}
              value={settings.margins.bottom}
              onChange={(e) => updateMargin("bottom", e.target.value)}
            />
          </div>
        </div>
        <div
          className="dimstyle-editor-row"
          style={{ paddingTop: 4, fontSize: 11, opacity: 0.85 }}
        >
          {t("print.dialog.preview_label")}
        </div>
        <div className="dimstyle-editor-actions">
          <button
            type="button"
            data-testid="print-preview-confirm"
            onClick={onConfirm}
          >
            {t("print.dialog.confirm")}
          </button>
          <button
            type="button"
            data-testid="print-preview-cancel"
            onClick={onCancel}
          >
            {t("print.dialog.cancel")}
          </button>
        </div>
      </div>
    </div>
  );
}

import type * as React from "react";
